import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function OrganizationDataExportCard() {
  const { userRole } = useAuth();
  const [busy, setBusy] = useState(false);

  if (userRole !== 'super_admin' && userRole !== 'program_director') return null;

  const handleExport = async () => {
    try {
      setBusy(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Not signed in');
        return;
      }
      const url = `https://qtkmvxjzitahoszmychk.supabase.co/functions/v1/export-organization-data`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition') || '';
      const match = dispo.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `organization-export-${new Date().toISOString()}.zip`;
      const link = document.createElement('a');
      const objUrl = URL.createObjectURL(blob);
      link.href = objUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
      toast.success('Export downloaded');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Data Export</CardTitle>
        <CardDescription>
          Download a ZIP containing CSVs of every table scoped to your organization
          (clients, check-ins, notes, service logs, alerts, documents, bed counts,
          family check-ins, activity logs, and more). The action is recorded in the
          activity log.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={handleExport} disabled={busy}>
          {busy ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export All Organization Data
        </Button>
      </CardContent>
    </Card>
  );
}
